import api from './api';

const maintenanceService = {
  // Get all maintenance records
  getMaintenanceRecords: async (params = {}) => {
    try {
      const response = await api.get('/maintenance', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance records:', error);
      throw error;
    }
  },

  // Get maintenance record by ID
  getMaintenanceById: async (id) => {
    try {
      const response = await api.get(`/maintenance/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching maintenance record ${id}:`, error);
      throw error;
    }
  },

  // Create new maintenance record
  createMaintenance: async (maintenanceData) => {
    try {
      const response = await api.post('/maintenance', maintenanceData);
      return response.data;
    } catch (error) {
      console.error('Error creating maintenance record:', error);
      throw error;
    }
  },

  // Update maintenance record
  updateMaintenance: async (id, maintenanceData) => {
    try {
      const response = await api.put(`/maintenance/${id}`, maintenanceData);
      return response.data;
    } catch (error) {
      console.error(`Error updating maintenance record ${id}:`, error);
      throw error;
    }
  },

  // Delete maintenance record
  deleteMaintenance: async (id) => {
    try {
      const response = await api.delete(`/maintenance/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance record ${id}:`, error);
      throw error;
    }
  },

  // Get maintenance types
  getMaintenanceTypes: async () => {
    try {
      const response = await api.get('/maintenance/types');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance types:', error);
      throw error;
    }
  },

  // Get maintenance statistics
  getMaintenanceStats: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/stats', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance statistics:', error);
      throw error;
    }
  },

  // Get vehicle maintenance history
  getVehicleMaintenanceHistory: async (vehicleId, params = {}) => {
    try {
      const response = await api.get(`/maintenance/vehicle/${vehicleId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching maintenance history for vehicle ${vehicleId}:`, error);
      throw error;
    }
  },

  // Get upcoming maintenance
  getUpcomingMaintenance: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/upcoming', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming maintenance:', error);
      throw error;
    }
  },

  // Get overdue maintenance
  getOverdueMaintenance: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/overdue', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching overdue maintenance:', error);
      throw error;
    }
  },

  // Schedule maintenance
  scheduleMaintenance: async (maintenanceData) => {
    try {
      const response = await api.post('/maintenance/schedule', maintenanceData);
      return response.data;
    } catch (error) {
      console.error('Error scheduling maintenance:', error);
      throw error;
    }
  },

  // Complete maintenance
  completeMaintenance: async (id, completionData) => {
    try {
      const response = await api.post(`/maintenance/${id}/complete`, completionData);
      return response.data;
    } catch (error) {
      console.error(`Error completing maintenance ${id}:`, error);
      throw error;
    }
  },

  // Cancel maintenance
  cancelMaintenance: async (id, reason) => {
    try {
      const response = await api.post(`/maintenance/${id}/cancel`, { reason });
      return response.data;
    } catch (error) {
      console.error(`Error canceling maintenance ${id}:`, error);
      throw error;
    }
  },

  // Get maintenance costs
  getMaintenanceCosts: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/costs', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance costs:', error);
      throw error;
    }
  },

  // Get maintenance reports
  getMaintenanceReports: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/reports', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance reports:', error);
      throw error;
    }
  },

  // Export maintenance records
  exportMaintenanceRecords: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/export', {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting maintenance records:', error);
      throw error;
    }
  },

  // Get maintenance alerts
  getMaintenanceAlerts: async () => {
    try {
      const response = await api.get('/maintenance/alerts');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance alerts:', error);
      throw error;
    }
  },

  // Create maintenance reminder
  createMaintenanceReminder: async (maintenanceId, reminderData) => {
    try {
      const response = await api.post(`/maintenance/${maintenanceId}/reminders`, reminderData);
      return response.data;
    } catch (error) {
      console.error(`Error creating maintenance reminder for ${maintenanceId}:`, error);
      throw error;
    }
  },

  // Get maintenance reminders
  getMaintenanceReminders: async (maintenanceId) => {
    try {
      const response = await api.get(`/maintenance/${maintenanceId}/reminders`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching maintenance reminders for ${maintenanceId}:`, error);
      throw error;
    }
  },

  // Update maintenance reminder
  updateMaintenanceReminder: async (reminderId, reminderData) => {
    try {
      const response = await api.put(`/maintenance/reminders/${reminderId}`, reminderData);
      return response.data;
    } catch (error) {
      console.error(`Error updating maintenance reminder ${reminderId}:`, error);
      throw error;
    }
  },

  // Delete maintenance reminder
  deleteMaintenanceReminder: async (reminderId) => {
    try {
      const response = await api.delete(`/maintenance/reminders/${reminderId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance reminder ${reminderId}:`, error);
      throw error;
    }
  },

  // Get maintenance performance metrics
  getMaintenancePerformanceMetrics: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/performance-metrics', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance performance metrics:', error);
      throw error;
    }
  },

  // Get maintenance trends
  getMaintenanceTrends: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/trends', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance trends:', error);
      throw error;
    }
  },

  // Get maintenance predictions
  getMaintenancePredictions: async (vehicleId) => {
    try {
      const response = await api.get(`/maintenance/predictions/${vehicleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching maintenance predictions for vehicle ${vehicleId}:`, error);
      throw error;
    }
  },

  // Get maintenance vendors
  getMaintenanceVendors: async () => {
    try {
      const response = await api.get('/maintenance/vendors');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance vendors:', error);
      throw error;
    }
  },

  // Create maintenance vendor
  createMaintenanceVendor: async (vendorData) => {
    try {
      const response = await api.post('/maintenance/vendors', vendorData);
      return response.data;
    } catch (error) {
      console.error('Error creating maintenance vendor:', error);
      throw error;
    }
  },

  // Update maintenance vendor
  updateMaintenanceVendor: async (id, vendorData) => {
    try {
      const response = await api.put(`/maintenance/vendors/${id}`, vendorData);
      return response.data;
    } catch (error) {
      console.error(`Error updating maintenance vendor ${id}:`, error);
      throw error;
    }
  },

  // Delete maintenance vendor
  deleteMaintenanceVendor: async (id) => {
    try {
      const response = await api.delete(`/maintenance/vendors/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance vendor ${id}:`, error);
      throw error;
    }
  },

  // Get maintenance parts
  getMaintenanceParts: async () => {
    try {
      const response = await api.get('/maintenance/parts');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance parts:', error);
      throw error;
    }
  },

  // Create maintenance part
  createMaintenancePart: async (partData) => {
    try {
      const response = await api.post('/maintenance/parts', partData);
      return response.data;
    } catch (error) {
      console.error('Error creating maintenance part:', error);
      throw error;
    }
  },

  // Update maintenance part
  updateMaintenancePart: async (id, partData) => {
    try {
      const response = await api.put(`/maintenance/parts/${id}`, partData);
      return response.data;
    } catch (error) {
      console.error(`Error updating maintenance part ${id}:`, error);
      throw error;
    }
  },

  // Delete maintenance part
  deleteMaintenancePart: async (id) => {
    try {
      const response = await api.delete(`/maintenance/parts/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance part ${id}:`, error);
      throw error;
    }
  },

  // Get maintenance work orders
  getMaintenanceWorkOrders: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/work-orders', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance work orders:', error);
      throw error;
    }
  },

  // Create maintenance work order
  createMaintenanceWorkOrder: async (workOrderData) => {
    try {
      const response = await api.post('/maintenance/work-orders', workOrderData);
      return response.data;
    } catch (error) {
      console.error('Error creating maintenance work order:', error);
      throw error;
    }
  },

  // Update maintenance work order
  updateMaintenanceWorkOrder: async (id, workOrderData) => {
    try {
      const response = await api.put(`/maintenance/work-orders/${id}`, workOrderData);
      return response.data;
    } catch (error) {
      console.error(`Error updating maintenance work order ${id}:`, error);
      throw error;
    }
  },

  // Delete maintenance work order
  deleteMaintenanceWorkOrder: async (id) => {
    try {
      const response = await api.delete(`/maintenance/work-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance work order ${id}:`, error);
      throw error;
    }
  },

  // Get maintenance checklists
  getMaintenanceChecklists: async () => {
    try {
      const response = await api.get('/maintenance/checklists');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance checklists:', error);
      throw error;
    }
  },

  // Create maintenance checklist
  createMaintenanceChecklist: async (checklistData) => {
    try {
      const response = await api.post('/maintenance/checklists', checklistData);
      return response.data;
    } catch (error) {
      console.error('Error creating maintenance checklist:', error);
      throw error;
    }
  },

  // Update maintenance checklist
  updateMaintenanceChecklist: async (id, checklistData) => {
    try {
      const response = await api.put(`/maintenance/checklists/${id}`, checklistData);
      return response.data;
    } catch (error) {
      console.error(`Error updating maintenance checklist ${id}:`, error);
      throw error;
    }
  },

  // Delete maintenance checklist
  deleteMaintenanceChecklist: async (id) => {
    try {
      const response = await api.delete(`/maintenance/checklists/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance checklist ${id}:`, error);
      throw error;
    }
  },

  // Get maintenance documents
  getMaintenanceDocuments: async (maintenanceId) => {
    try {
      const response = await api.get(`/maintenance/${maintenanceId}/documents`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching maintenance documents for ${maintenanceId}:`, error);
      throw error;
    }
  },

  // Upload maintenance document
  uploadMaintenanceDocument: async (maintenanceId, documentData) => {
    try {
      const formData = new FormData();
      
      Object.keys(documentData).forEach(key => {
        if (key === 'file') {
          formData.append('file', documentData.file);
        } else {
          formData.append(key, documentData[key]);
        }
      });

      const response = await api.post(`/maintenance/${maintenanceId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading maintenance document for ${maintenanceId}:`, error);
      throw error;
    }
  },

  // Delete maintenance document
  deleteMaintenanceDocument: async (maintenanceId, documentId) => {
    try {
      const response = await api.delete(`/maintenance/${maintenanceId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting maintenance document ${documentId}:`, error);
      throw error;
    }
  },

  // Get maintenance notifications
  getMaintenanceNotifications: async (params = {}) => {
    try {
      const response = await api.get('/maintenance/notifications', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance notifications:', error);
      throw error;
    }
  },

  // Mark maintenance notification as read
  markMaintenanceNotificationAsRead: async (notificationId) => {
    try {
      const response = await api.post(`/maintenance/notifications/${notificationId}/mark-read`);
      return response.data;
    } catch (error) {
      console.error(`Error marking maintenance notification ${notificationId} as read:`, error);
      throw error;
    }
  },

  // Get maintenance dashboard data
  getMaintenanceDashboardData: async () => {
    try {
      const response = await api.get('/maintenance/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance dashboard data:', error);
      throw error;
    }
  }
};

export default maintenanceService;  